import React from 'react';

type CsvExportContextValue = {
  startExport: (label: string, task: () => Promise<void>) => Promise<void>;
};

const CsvExportContext = React.createContext<CsvExportContextValue | null>(null);

export const CsvExportProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [activeLabel, setActiveLabel] = React.useState<string | null>(null);

  const startExport = React.useCallback(
    async (label: string, task: () => Promise<void>) => {
      setActiveLabel(label);
      try {
        await task();
      } finally {
        setActiveLabel(null);
      }
    },
    []
  );

  const value = React.useMemo(() => ({ startExport }), [startExport]);

  return (
    <CsvExportContext.Provider value={value}>
      {children}
      {activeLabel ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            top: 12,
            right: 12,
            zIndex: 10050,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: 'rgba(255,255,255,0.96)',
            border: '1px solid #EAEAEA',
            borderRadius: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            fontSize: 14,
            maxWidth: 360,
          }}
        >
          <span
            style={{
              width: 18,
              height: 18,
              border: '2px solid #c5a231',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'tb-csv-spin 0.7s linear infinite',
            }}
          />
          <span>Compiling CSV for {activeLabel}</span>
          <style>{`@keyframes tb-csv-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : null}
    </CsvExportContext.Provider>
  );
};

export function useCsvExport(): CsvExportContextValue | null {
  return React.useContext(CsvExportContext);
}
